import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from "react";
const MAX_WORKSPACE_NAME_LENGTH = 50;
function truncateWorkspaceName(name) {
    if (name.length <= MAX_WORKSPACE_NAME_LENGTH) {
        return name;
    }
    return `${name.slice(0, MAX_WORKSPACE_NAME_LENGTH - 3)}...`;
}
function WorkspaceSwitcherComponent({ currentWorkspaceId, workspaces, onWorkspaceSelect, isLoading = false, error = null, className = "", }) {
    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const shouldShowSearch = workspaces.length > 10;
    const currentWorkspace = useMemo(() => workspaces.find((workspace) => workspace.id === currentWorkspaceId), [currentWorkspaceId, workspaces]);
    useEffect(() => {
        const timer = window.setTimeout(() => {
            setSearchQuery(searchInput.trim());
        }, 300);
        return () => window.clearTimeout(timer);
    }, [searchInput]);
    const filteredWorkspaces = useMemo(() => {
        if (!shouldShowSearch || !searchQuery) {
            return workspaces;
        }
        const query = searchQuery.toLowerCase();
        return workspaces.filter((workspace) => workspace.name.toLowerCase().includes(query));
    }, [searchQuery, shouldShowSearch, workspaces]);
    const closeDropdown = (restoreFocus = true) => {
        setIsOpen(false);
        setHighlightedIndex(-1);
        if (restoreFocus) {
            triggerRef.current?.focus();
        }
    };
    const openDropdown = () => {
        setIsOpen(true);
        const currentIndex = filteredWorkspaces.findIndex((workspace) => workspace.id === currentWorkspaceId);
        setHighlightedIndex(currentIndex >= 0 ? currentIndex : filteredWorkspaces.length > 0 ? 0 : -1);
    };
    const toggleDropdown = () => {
        if (isOpen) {
            closeDropdown(false);
            return;
        }
        openDropdown();
    };
    const handleSelect = (workspaceId) => {
        onWorkspaceSelect(workspaceId);
        closeDropdown();
    };
    const focusOption = (index) => {
        const optionElement = dropdownRef.current?.querySelector(`[data-workspace-index="${index}"]`);
        optionElement?.focus();
    };
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        if (shouldShowSearch) {
            searchInputRef.current?.focus();
            return;
        }
        if (highlightedIndex >= 0) {
            focusOption(highlightedIndex);
        }
    }, [highlightedIndex, isOpen, shouldShowSearch]);
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const handlePointerDown = (event) => {
            const target = event.target;
            if (!wrapperRef.current?.contains(target)) {
                closeDropdown(false);
            }
        };
        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("touchstart", handlePointerDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("touchstart", handlePointerDown);
        };
    }, [isOpen]);
    const handleDropdownKeyDown = (event) => {
        if (!isOpen) {
            return;
        }
        if (event.key === "Escape") {
            event.preventDefault();
            closeDropdown();
            return;
        }
        if (event.key === "ArrowDown") {
            event.preventDefault();
            if (filteredWorkspaces.length === 0) {
                return;
            }
            const nextIndex = highlightedIndex < filteredWorkspaces.length - 1 ? highlightedIndex + 1 : 0;
            setHighlightedIndex(nextIndex);
            focusOption(nextIndex);
            return;
        }
        if (event.key === "ArrowUp") {
            event.preventDefault();
            if (filteredWorkspaces.length === 0) {
                return;
            }
            const nextIndex = highlightedIndex > 0 ? highlightedIndex - 1 : filteredWorkspaces.length - 1;
            setHighlightedIndex(nextIndex);
            focusOption(nextIndex);
            return;
        }
        if (event.key === "Enter") {
            if (highlightedIndex >= 0 && highlightedIndex < filteredWorkspaces.length) {
                event.preventDefault();
                handleSelect(filteredWorkspaces[highlightedIndex].id);
            }
            return;
        }
        if (event.key === "Tab") {
            const focusables = dropdownRef.current?.querySelectorAll('button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])');
            if (!focusables || focusables.length === 0) {
                event.preventDefault();
                return;
            }
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement;
            if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
            else if (event.shiftKey && active === first) {
                event.preventDefault();
                last.focus();
            }
        }
    };
    const handleTriggerKeyDown = (event) => {
        if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (!isOpen) {
                openDropdown();
            }
        }
        if (event.key === "Escape" && isOpen) {
            event.preventDefault();
            closeDropdown();
        }
    };
    const triggerLabel = currentWorkspace ? truncateWorkspaceName(currentWorkspace.name) : "No workspace selected";
    return (_jsxs("div", { ref: wrapperRef, className: `relative ${className}`, children: [isOpen && (_jsx("button", { type: "button", "aria-label": "Close workspace switcher", className: "fixed inset-0 z-40 bg-black/50 md:hidden", onClick: () => closeDropdown(false) })), _jsxs("button", { ref: triggerRef, type: "button", className: "touch-target border-border bg-surface text-textPrimary inline-flex min-h-11 min-w-[220px] items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm", onClick: toggleDropdown, onKeyDown: handleTriggerKeyDown, "aria-label": currentWorkspace ? `Current workspace ${currentWorkspace.name}` : "No workspace selected", "aria-expanded": isOpen, "aria-haspopup": "listbox", children: [_jsx("span", { className: "truncate", title: currentWorkspace?.name, children: triggerLabel }), _jsx("svg", { className: `h-4 w-4 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", "aria-hidden": "true", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M19 9l-7 7-7-7" }) })] }), isOpen && (_jsxs("div", { ref: dropdownRef, className: "bg-surface border-border absolute left-0 top-[calc(100%+0.5rem)] z-50 w-full min-w-[260px] rounded-md border shadow-lg", onKeyDown: handleDropdownKeyDown, children: [shouldShowSearch && (_jsx("div", { className: "border-border border-b p-2", children: _jsx("input", { ref: searchInputRef, type: "search", className: "touch-target border-border text-textPrimary w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black", placeholder: "Search workspaces...", value: searchInput, onChange: (event) => setSearchInput(event.target.value), "aria-label": "Search workspaces" }) })), error ? (_jsx("div", { className: "p-3 text-sm text-red-700", children: "Failed to load workspaces. Try again." })) : isLoading ? (_jsxs("div", { className: "space-y-2 p-3", children: [_jsx("div", { className: "skeleton h-10 rounded-md" }), _jsx("div", { className: "skeleton h-10 rounded-md" }), _jsx("div", { className: "skeleton h-10 rounded-md" })] })) : filteredWorkspaces.length === 0 ? (_jsx("div", { className: "text-textSecondary p-3 text-sm", children: "No workspaces found." })) : (_jsx("ul", { role: "listbox", "aria-label": "Workspace list", className: "max-h-72 overflow-y-auto p-1", children: filteredWorkspaces.map((workspace, index) => {
                            const isActive = workspace.id === currentWorkspaceId;
                            const isHighlighted = index === highlightedIndex;
                            return (_jsx("li", { role: "none", children: _jsxs("button", { type: "button", role: "option", "aria-selected": isActive, "data-workspace-index": index, onFocus: () => setHighlightedIndex(index), onMouseEnter: () => setHighlightedIndex(index), onClick: () => handleSelect(workspace.id), className: `touch-target flex min-h-11 w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${isActive
                                        ? "bg-black/10 text-black"
                                        : isHighlighted
                                            ? "bg-black/5 text-black"
                                            : "text-textSecondary hover:bg-black/5 hover:text-black"}`, title: workspace.name, children: [_jsx("span", { className: "truncate", children: truncateWorkspaceName(workspace.name) }), workspace.role ? (_jsx("span", { className: "ml-2 flex-shrink-0 text-xs uppercase tracking-wide text-black/60", children: workspace.role })) : null] }) }, workspace.id));
                        }) }))] }))] }));
}
export const WorkspaceSwitcher = React.memo(WorkspaceSwitcherComponent);
